import { Alert, Button, Stack } from '@mui/material'
import type { FC } from 'react'
import { useEffect, useState } from 'react'
import type { SubmitHandler } from 'react-hook-form'
import { useForm } from 'react-hook-form'

import MuiTextAreaField from '@/components/Forms/MuiTextAreaField'
import FormStack from '@/components/UI/FormContainer'
import { genericErrorMessage } from '@/utils/error'

type Fields = {
  reason: string
}

export type Props = {
  isEscalation?: boolean
  onEditEscalation: (fields: Fields) => Promise<any>
  onCancel: () => void
}

const EscalateApplicantForm: FC<Props> = ({
  isEscalation = true,
  onEditEscalation,
  onCancel,
}) => {
  const {
    control,
    handleSubmit,
    watch,
    formState: { isValid },
  } = useForm<Fields>({
    defaultValues: {
      reason: '',
    },
  })

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined)

  useEffect(() => {
    const subscription = watch(() => {
      setErrorMessage(undefined)
    })

    return () => subscription.unsubscribe()
  }, [watch])

  const onSubmit: SubmitHandler<Fields> = async data => {
    setIsLoading(true)
    setErrorMessage(undefined)

    try {
      await onEditEscalation(data)
    } catch (e) {
      setErrorMessage(genericErrorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <FormStack>
        <MuiTextAreaField<Fields>
          id="reason"
          name="reason"
          label="Reason"
          rules={{ required: true }}
          control={control}
        />
        <Stack direction="row" justifyContent="end" spacing={3}>
          <Button size="medium" sx={{ lineHeight: 'inherit' }} onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type="submit"
            name="create"
            variant="contained"
            disabled={!isValid}
            loading={isLoading}
            sx={{ width: 200, lineHeight: 'inherit' }}
          >
            {isEscalation ? <>Escalate</> : <>De-escalate</>}
          </Button>
        </Stack>
        {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
      </FormStack>
    </form>
  )
}

export default EscalateApplicantForm
